import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import Checkbox from '../../../components/Checkbox';
import EmptyState from '../../../components/EmptyState';
import TableHeaderCell from '../../../components/TableHeaderCell';
import localeLookup from '../../../config/locale';
import ElementStatusV2Row from './ElementStatusV2Row';

const columnWidths = [40, 340, 170, 40, 170, 170, 250, 170, 300, 300, 300];
const tableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
const checkboxHeaderCellStyle = {
  height: '40px',
  minHeight: '40px',
  width: '100%',
};

const Scroller = React.forwardRef((props, ref) => (
  <div
    {...props}
    className={`status-table__table-scroller${
      props.className ? ` ${props.className}` : ''
    }`}
    ref={ref}
    data-testid="element-status-v2-table-scroller"
    style={{
      flex: '1 1 auto',
      ...props.style,
      minHeight: 0,
      overflowX: 'auto',
      overflowY: 'auto',
      width: '100%',
    }}
  />
));

Scroller.displayName = 'ElementStatusV2TableScroller';

const Table = React.forwardRef((props, ref) => (
  <table
    {...props}
    className={`status-table__table-element${
      props.className ? ` ${props.className}` : ''
    }`}
    ref={ref}
    data-testid="element-status-v2-table-element"
    style={{
      ...props.style,
      borderSpacing: 0,
      overflowAnchor: 'none',
      width: `${tableWidth}px`,
    }}
  />
));

Table.displayName = 'ElementStatusV2TableElement';

const TableHead = React.forwardRef((props, ref) => (
  <thead
    {...props}
    className={`status-table__table-head${
      props.className ? ` ${props.className}` : ''
    }`}
    ref={ref}
    style={{ ...props.style, zIndex: 4 }}
  />
));

TableHead.displayName = 'ElementStatusV2TableHead';

const TableRow = React.forwardRef((props, ref) => (
  <tr {...props} ref={ref} style={{ ...props.style, height: '40px' }} />
));

TableRow.displayName = 'ElementStatusV2TableRow';

const getStickyHeaderStyle = (index) => {
  if (index === 0)
    return {
      left: 0,
      padding: 0,
      position: 'sticky',
      width: columnWidths[0],
      zIndex: 3,
    };
  if (index === 1)
    return {
      left: columnWidths[0],
      padding: 0,
      position: 'sticky',
      width: columnWidths[1],
      zIndex: 3,
    };
  if (index === 2)
    return {
      left: columnWidths[0] + columnWidths[1],
      padding: 0,
      position: 'sticky',
      width: columnWidths[2],
      zIndex: 3,
    };
  if (index === 3)
    return {
      left: columnWidths[0] + columnWidths[1] + columnWidths[2],
      padding: 0,
      position: 'sticky',
      width: columnWidths[3],
      zIndex: 3,
    };

  return { padding: 0, width: columnWidths[index] };
};

const getHeaderCellClassName = (index) =>
  index <= 3
    ? 'status-table__table-header-cell status-table__table-header-cell--sticky'
    : 'status-table__table-header-cell';

export default function ElementStatusV2Table({
  onLoadMore = () => {},
  onOpenElementContent = () => {},
  onOpenDetails = () => {},
  onOpenHistory = () => {},
  onRefreshRow = () => {},
  rows = [],
  onSortChange = () => {},
  selectedRows = {},
  sorting = { columnId: 'elementName', direction: 'asc' },
  toggleAllLoadedRows = () => {},
  toggleRowSelection = () => {},
}) {
  const allLoadedRowsSelected =
    rows.length > 0 &&
    rows.every((row) =>
      (selectedRows[row.personId] || []).includes(row.elementId)
    );

  if (rows.length === 0) {
    return (
      <div className="status-table__table-no-content">
        <EmptyState
          fullHeight
          title={localeLookup('translations.No elements found')}
          body={localeLookup(
            'translations.The selected filters contains no elements'
          )}
        />
      </div>
    );
  }

  return (
    <div
      className="status-table__table-grid"
      data-testid="element-status-v2-table-grid"
      style={{ flex: '1 1 auto', minHeight: 0 }}
    >
      <TableVirtuoso
        components={{ Scroller, Table, TableHead, TableRow }}
        data={rows}
        endReached={() => onLoadMore()}
        style={{
          flex: '1 1 auto',
          height: '100%',
          minHeight: 0,
          width: '100%',
        }}
        fixedHeaderContent={() => (
          <tr>
            <th
              className={getHeaderCellClassName(0)}
              scope="col"
              style={getStickyHeaderStyle(0)}
            >
              <TableHeaderCell centerContent style={checkboxHeaderCellStyle}>
                <Checkbox
                  id="select-all-loaded-rows"
                  isChecked={allLoadedRowsSelected}
                  onChange={() => toggleAllLoadedRows()}
                />
              </TableHeaderCell>
            </th>
            <th
              className={getHeaderCellClassName(1)}
              scope="col"
              style={getStickyHeaderStyle(1)}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'elementName'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('elementName')}
                title={localeLookup('translations.Element')}
              />
            </th>
            <th
              className={getHeaderCellClassName(2)}
              scope="col"
              style={getStickyHeaderStyle(2)}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'personName'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('personName')}
                title={localeLookup('translations.Person')}
              />
            </th>
            <th
              className={getHeaderCellClassName(3)}
              scope="col"
              style={getStickyHeaderStyle(3)}
            >
              <TableHeaderCell title={''} />
            </th>
            <th
              className={getHeaderCellClassName(4)}
              scope="col"
              style={{ width: columnWidths[4] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'responsible'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('responsible')}
                title={localeLookup('translations.Responsible')}
              />
            </th>
            <th
              className={getHeaderCellClassName(5)}
              scope="col"
              style={{ width: columnWidths[5] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'mentor'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('mentor')}
                title={localeLookup('translations.Mentor')}
              />
            </th>
            <th
              className={getHeaderCellClassName(6)}
              scope="col"
              style={{ width: columnWidths[6] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'latestActivity'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('latestActivity')}
                title={localeLookup('translations.Latest activity')}
              />
            </th>
            <th
              className={getHeaderCellClassName(7)}
              scope="col"
              style={{ width: columnWidths[7] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'expirationDate'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('expirationDate')}
                title={localeLookup('translations.Expiration date')}
              />
            </th>
            <th
              className={getHeaderCellClassName(8)}
              scope="col"
              style={{ width: columnWidths[8] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'organisationUnit'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('organisationUnit')}
                title={localeLookup('translations.Organisation unit')}
              />
            </th>
            <th
              className={getHeaderCellClassName(9)}
              scope="col"
              style={{ width: columnWidths[9] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'role'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('role')}
                title={localeLookup('translations.Role')}
              />
            </th>
            <th
              className={getHeaderCellClassName(10)}
              scope="col"
              style={{ width: columnWidths[10] }}
            >
              <TableHeaderCell
                sortActive={sorting.columnId === 'module'}
                sortDirection={sorting.direction}
                sortable
                onClick={() => onSortChange('module')}
                title={localeLookup('translations.Knowledge area')}
              />
            </th>
          </tr>
        )}
        itemContent={(index, row) => (
          <ElementStatusV2Row
            onOpenElementContent={onOpenElementContent}
            onOpenDetails={onOpenDetails}
            onOpenHistory={onOpenHistory}
            onRefreshRow={onRefreshRow}
            row={row}
            selectedRows={selectedRows}
            toggleRowSelection={toggleRowSelection}
          />
        )}
      />
    </div>
  );
}
